import { useCallback, useEffect, useRef, useState } from 'react';

import {
  StoreRegion,
  useGetDealByIdLazyQuery,
} from '@/lib/graphql/generated/graphql';
import { trackEvent } from '@/lib/mixpanel';
import { useAtomValue, useSetAtom, useStore } from 'jotai';

import { dealActiveVersionState } from '../../states/deal-active-version.state';
import { dealStateState } from '../../states/deal-state.state';
import {
  dealAtom,
  dealErrorAtom,
  dealLoadedForIdAtom,
  dealRefetchAtom,
} from '../../states/deal.state';
import { editionModeState } from '../../states/edition-mode.state';
import { hasCouponCodesFileState } from '../../states/has-coupon-codes-file.state';
import { pricingModeState } from '../../states/pricing-mode.state';
import { storeAtom } from '../../states/store.state';
import { Store } from '../../types/store';

export const useDeal = (dealId: string) => {
  const setDeal = useSetAtom(dealAtom);
  const setDealError = useSetAtom(dealErrorAtom);
  const setLoadedForId = useSetAtom(dealLoadedForIdAtom);
  const setRefetch = useSetAtom(dealRefetchAtom);
  const setStore = useSetAtom(storeAtom);
  const setHasCouponCodesFile = useSetAtom(hasCouponCodesFileState);
  const setEditionMode = useSetAtom(editionModeState);
  const setDealState = useSetAtom(dealStateState);
  const setPricingMode = useSetAtom(pricingModeState);
  const setActiveVersion = useSetAtom(dealActiveVersionState);
  const [hasResolvedInitialDealQuery, setHasResolvedInitialDealQuery] =
    useState(false);

  const [executeFetch, { data, loading, error }] = useGetDealByIdLazyQuery({
    fetchPolicy: 'no-cache',
  });

  const jotaiStore = useStore();
  const trackedDealViewedForIdRef = useRef<string | null>(null);

  // Initial fetch: skip if we already have data for this dealId (navigation remount).
  // Clear atom first when navigating to a different deal to avoid showing stale data.
  useEffect(() => {
    if (!dealId) return;

    const loadedForId = jotaiStore.get(dealLoadedForIdAtom);
    const hasDeal = jotaiStore.get(dealAtom) !== null;

    if (loadedForId !== dealId) {
      setDeal(null);
      setDealError(undefined);
      void executeFetch({ variables: { dealId } });
    } else if (!hasDeal) {
      void executeFetch({ variables: { dealId } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId]);

  // Store refetch in atom so other hooks can trigger a refresh
  useEffect(() => {
    if (!dealId) return;
    setRefetch({
      fn: async () => {
        await executeFetch({ variables: { dealId } });
      },
    });
  }, [dealId, executeFetch, setRefetch]);

  // Sync error atom so consumers (e.g. useLatestPreviousCampaigns) can distinguish loading from failed
  useEffect(() => {
    setDealError(error);
  }, [error, setDealError]);

  // Sync atom when new data arrives
  useEffect(() => {
    if (data?.deal) {
      setDeal(data.deal);
      setLoadedForId(dealId);
      setActiveVersion(data.deal.activeVersion);
      // Convert StoreRegion (from backend) to Store (frontend enum)
      // StoreRegion uses Fr/Us, Store uses FR/US - same values, different casing
      const storeValue =
        data.deal.hubspotInfos.store === StoreRegion.Fr ? Store.FR : Store.US;
      setStore(storeValue);
      setHasCouponCodesFile(data.deal.hasCouponCodesFile ?? false);
      setEditionMode(data.deal.editionMode);
      setDealState(data.deal.state);
      setPricingMode(data.deal.pricingMode);

      if (trackedDealViewedForIdRef.current !== dealId) {
        trackedDealViewedForIdRef.current = dealId;
        trackEvent('DEAL_VIEWED', { dealId });
      }
    }
  }, [
    data,
    dealId,
    setDeal,
    setLoadedForId,
    setStore,
    setHasCouponCodesFile,
    setEditionMode,
    setDealState,
    setPricingMode,
    setActiveVersion,
  ]);

  const atomDeal = useAtomValue(dealAtom);
  const loadedId = useAtomValue(dealLoadedForIdAtom);
  // Don't expose stale deal data from a different dealId (avoids one-render flash on navigation)
  const deal = loadedId === dealId ? atomDeal : null;

  useEffect(() => {
    if (loading) {
      setHasResolvedInitialDealQuery(false);
      return;
    }
    const timer = setTimeout(() => {
      setHasResolvedInitialDealQuery(true);
    }, 2_000);
    return () => clearTimeout(timer);
  }, [loading, dealId]);

  const initialLoading = dealId
    ? !hasResolvedInitialDealQuery && !deal && !error
    : false;

  return {
    deal,
    loading,
    error,
    refetch: useCallback(
      () => executeFetch({ variables: { dealId } }),
      [executeFetch, dealId]
    ),
    initialLoading,
  };
};
